import React, { useEffect, useState, Children } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './i18n';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
import { PricesPage } from './pages/PricesPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { CookiePolicyPage } from './pages/CookiePolicyPage';
import { PatientPrivacyNoticePage } from './pages/PatientPrivacyNoticePage';
import { TermsAndConditionsPage } from './pages/TermsAndConditionsPage';
import { BusinessCardPage } from './pages/BusinessCardPage';
import { BusinessCardNewPage } from './pages/BusinessCardNewPage';
import { BusinessCardNewUaPage } from './pages/BusinessCardNewUaPage';
import { BusinessCardNewRuPage } from './pages/BusinessCardNewRuPage';
import { UkrainianDentistPage } from './pages/UkrainianDentistPage';
import { ImplantyLondonPage } from './pages/ImplantyLondonPage';
import { VeneersLondonPage } from './pages/VeneersLondonPage';
import { RootCanalLondonPage } from './pages/RootCanalLondonPage';
import { EmergencyDentistLondonPage } from './pages/EmergencyDentistLondonPage';
import { RussianDentistPage } from './pages/RussianDentistPage';
import { ImplantyLondonRuPage } from './pages/ImplantyLondonRuPage';
import { VeneersLondonRuPage } from './pages/VeneersLondonRuPage';
import { RootCanalLondonRuPage } from './pages/RootCanalLondonRuPage';
import { EmergencyDentistLondonRuPage } from './pages/EmergencyDentistLondonRuPage';
import { BlogIndexPage } from './pages/blog/BlogIndexPage';
import { ImplantyCostArticle } from './pages/blog/ImplantyCostArticle';
import { NhsVsPrivateArticle } from './pages/blog/NhsVsPrivateArticle';
import { WithoutInsuranceArticle } from './pages/blog/WithoutInsuranceArticle';
import { ToothPainArticle } from './pages/blog/ToothPainArticle';
import { VeneersGuideArticle } from './pages/blog/VeneersGuideArticle';
import { ChooseDentistArticle } from './pages/blog/ChooseDentistArticle';
import { SitemapPage } from './pages/SitemapPage';
import { OrthodonticsPage } from './pages/OrthodonticsPage';
import { ClearAlignersPage } from './pages/ClearAlignersPage';
import { InvisalignAlignersPage } from './pages/InvisalignAlignersPage';
import { MetalBracesPage } from './pages/MetalBracesPage';
import { CeramicBracesPage } from './pages/CeramicBracesPage';
import { RetainersPage } from './pages/RetainersPage';
import { ChildrensOrthodonticsPage } from './pages/ChildrensOrthodonticsPage';
import { OrthodonticAppliancesPage } from './pages/OrthodonticAppliancesPage';
import { HygienePage } from './pages/HygienePage';
import { GiftCertificate } from './components/GiftCertificate';
import { PrintPricePage } from './pages/PrintPricePage';
import { InfluencerInvitePage } from './pages/InfluencerInvitePage';
import { RaffleVoucherPage } from './pages/RaffleVoucherPage';
import { LogoPage } from './pages/LogoPage';
import { OrthodonticFlyerUaPage } from './pages/OrthodonticFlyerUaPage';
import { PaediatricDentistryPage } from './pages/PaediatricDentistryPage';
import { WhatsAppQrPage } from './pages/WhatsAppQrPage';
import { PatientExperiencePage } from './pages/PatientExperiencePage';
import { ResultsPage } from './pages/ResultsPage';
import { ReviewsPage } from './pages/ReviewsPage';
import { AiSupportPage } from './pages/AiSupportPage';
// ── Global scroll-to-top on every route change ──
// Exception: /prices#anchor — let PricesPage handle its own hash scrolling
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    // Disable browser scroll restoration globally
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);
  useEffect(() => {
    // If navigating to /prices with a hash, skip — PricesPage handles it
    const isPricesWithHash = pathname.includes('/prices') && hash;
    if (isPricesWithHash) return;
    // Other pages with #anchor: scroll to the section below the sticky header.
    // Retries briefly so it works after client-side navigation and direct loads.
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let attempts = 0;
      const timers: number[] = [];
      const scrollToTarget = (el: HTMLElement) => {
        const header = document.querySelector('header');
        const offset = (header?.getBoundingClientRect().height ?? 80) + 16;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: 'auto'
        });
      };
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          scrollToTarget(el);
          // Re-align once more in case images or fonts shifted the layout
          timers.push(window.setTimeout(() => scrollToTarget(el), 300));
          return;
        }
        if (attempts++ < 20) {
          timers.push(window.setTimeout(tryScroll, 50));
        } else {
          window.scrollTo(0, 0);
        }
      };
      tryScroll();
      return () => timers.forEach(timer => window.clearTimeout(timer));
    }
    // Immediate scroll
    window.scrollTo(0, 0);
    // Reliable delayed scroll for dynamic content / mobile
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
    const t = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 50);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}
// Detect user's preferred language from browser
function detectUserLanguage(): 'en' | 'ua' | 'ru' | 'ro' {
  const browserLangs = navigator.languages || [navigator.language];
  for (const lang of browserLangs) {
    const langCode = lang.toLowerCase().split('-')[0];
    if (langCode === 'uk' || langCode === 'ua') {
      return 'ua';
    }
    if (langCode === 'ru') {
      return 'ru';
    }
    if (langCode === 'ro') {
      return 'ro';
    }
    if (langCode === 'en') {
      return 'en';
    }
  }
  return 'en';
}
function getStoredLanguage(): 'en' | 'ua' | 'ru' | 'ro' | null {
  try {
    const stored = localStorage.getItem('dentico-preferred-lang');
    if (stored === 'en' || stored === 'ua' || stored === 'ru' || stored === 'ro') {
      return stored as 'en' | 'ua' | 'ru' | 'ro';
    }
  } catch {
    // localStorage not available
  }
  return null;
}
function setStoredLanguage(lang: 'en' | 'ua' | 'ru' | 'ro') {
  try {
    localStorage.setItem('dentico-preferred-lang', lang);
  } catch {
    // localStorage not available
  }
}
function LanguageRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const storedLang = getStoredLanguage();
    if (storedLang) {
      navigate(`/${storedLang}`, {
        replace: true
      });
      return;
    }
    const detectedLang = detectUserLanguage();
    setStoredLanguage(detectedLang);
    navigate(`/${detectedLang}`, {
      replace: true
    });
  }, [navigate]);
  return null;
}
function LanguageRoutes() {
  const {
    lang
  } = useParams();
  const validLangs = ['en', 'ua', 'ru', 'ro'];
  if (!validLangs.includes(lang || '')) {
    return <Navigate to="/en" replace data-id="element-0" data-mp="3d4jp0y9eeevn" />;
  }
  useEffect(() => {
    if (lang && validLangs.includes(lang)) {
      setStoredLanguage(lang as 'en' | 'ua' | 'ru' | 'ro');
    }
  }, [lang]);
  return <LanguageProvider data-id="element-1" data-mp="2zbajx9shgfix">
      <Routes data-id="element-2" data-mp="344dglqsk453c">
        <Route index element={<HomePage data-id="element-4" data-mp="345estylb6xmn" />} data-id="element-3" data-mp="3443sk8r2o80a" />
        <Route path="ukrainskyi-stomatolog-london" element={<UkrainianDentistPage data-id="element-6" data-mp="2pga747fb2b9s" />} data-id="element-5" data-mp="345yd3lyfly3u" />
        <Route path="implanty-london" element={<ImplantyLondonPage data-id="element-8" data-mp="2uf3oiaxpqmf8" />} data-id="element-7" data-mp="2ue21d83fa9h5" />
        <Route path="veneers-london" element={<VeneersLondonPage data-id="element-10" data-mp="2gej6vi3jawnb" />} data-id="element-9" data-mp="2uebrcvogwgmu" />
        <Route path="root-canal-london" element={<RootCanalLondonPage data-id="element-12" data-mp="2ggdp2onf8anp" />} data-id="element-11" data-mp="2gfc6lz42slvy" />
        <Route path="emergency-dentist-london" element={<EmergencyDentistLondonPage data-id="element-14" data-mp="2khp9mfrfu5p1" />} data-id="element-13" data-mp="2kge3486lyju8" />
        <Route path="russkij-stomatolog-london" element={<RussianDentistPage data-id="element-16" data-mp="2vwk5edsex22u" />} data-id="element-15" data-mp="30uc9s7a8ugnx" />
        <Route path="implantaciya-london" element={<ImplantyLondonRuPage data-id="element-18" data-mp="2vufzxrev7v0g" />} data-id="element-17" data-mp="2vxlsjgmpdf0x" />
        <Route path="viniry-london" element={<VeneersLondonRuPage data-id="element-20" data-mp="2qvcy0fu6m4wp" />} data-id="element-19" data-mp="2qwekdggnedtq" />
        <Route path="lechenie-kanalov-london" element={<RootCanalLondonRuPage data-id="element-22" data-mp="2lxuzzkhpda3h" />} data-id="element-21" data-mp="2ly4mgy3jgz4f" />
        <Route path="neotlozhnaya-stomatologiya-london" element={<EmergencyDentistLondonRuPage data-id="element-24" data-mp="2hycaebexsaub" />} data-id="element-23" data-mp="2hx1aimom3n8h" />
        <Route path="blog" element={<BlogIndexPage data-id="element-26" data-mp="2d3pzsagoimba" />} data-id="element-25" data-mp="2d513ybe1dw2x" />
        <Route path="blog/dental-implants-cost-london" element={<ImplantyCostArticle data-id="element-28" data-mp="284lu3tsyr6pp" />} data-id="element-27" data-mp="286ge95weuusq" />
        <Route path="blog/nhs-vs-private-dentistry-uk" element={<NhsVsPrivateArticle data-id="element-30" data-mp="1y8iouf2teryj" />} data-id="element-29" data-mp="238ejhnpmh71f" />
        <Route path="blog/dental-treatment-uk-without-insurance" element={<WithoutInsuranceArticle data-id="element-32" data-mp="1u6otmmbe2g5r" />} data-id="element-31" data-mp="1y78p7lgbdbps" />
        <Route path="blog/toothache-london-what-to-do" element={<ToothPainArticle data-id="element-34" data-mp="9ocwkvt5dk8i" />} data-id="element-33" data-mp="1u91izkjh7ssu" />
        <Route path="blog/veneers-london-everything-you-need-to-know" element={<VeneersGuideArticle data-id="element-36" data-mp="dsc9l4f7y0x4" />} data-id="element-35" data-mp="dqhnhms7o2rg" />
        <Route path="blog/how-to-choose-dentist-angel" element={<ChooseDentistArticle data-id="element-38" data-mp="nofbqaa2lzk2" />} data-id="element-37" data-mp="ipb3pmyvu7vb" />
        <Route path="sitemap" element={<SitemapPage data-id="element-40" data-mp="nmkxtfx7v7ph" />} data-id="element-39" data-mp="nncnyb81od8d" />
        <Route path="services" element={<ServicesPage data-id="element-42" data-mp="3m7cjqjei6owk" />} data-id="element-41" data-mp="3m6kx3y2vw1if" />
        <Route path="services/hygiene" element={<HygienePage data-id="element-44" data-mp="3r54hvv5qr797" />} data-id="element-43" data-mp="3m84c7lct06im" />
        <Route path="services/paediatric-dentistry" element={<PaediatricDentistryPage data-id="element-46" data-mp="3w4qty19u2hgf" />} data-id="element-45" data-mp="3r6fl9tv9yczs" />
        <Route path="services/orthodontics" element={<OrthodonticsPage data-id="element-48" data-mp="4pjhx024jn6r" />} data-id="element-47" data-mp="3w2wcwy1on9hm" />
        {/* Orthodontic Appliances — one localized slug per language */}
        <Route path="services/orthodontic-appliances" element={<OrthodonticAppliancesPage data-id="element-50" data-mp="33dz3zljd2p1j" />} data-id="element-49" data-mp="33g33lra891w0" />
        <Route path="services/ortodontychni-aparaty" element={<OrthodonticAppliancesPage data-id="element-52" data-mp="2f7djskc0kwln" />} data-id="element-51" data-mp="38duxurych03d" />
        <Route path="services/ortodonticheskie-apparaty" element={<OrthodonticAppliancesPage data-id="element-54" data-mp="2k56sbk0si2p3" />} data-id="element-53" data-mp="2f6ln166oksts" />
        <Route path="services/aparate-ortodontice" element={<OrthodonticAppliancesPage data-id="element-56" data-mp="2p5uf1thzjxdi" />} data-id="element-55" data-mp="2k81crbjsznii" />
        <Route path="services/orthodontics/clear-aligners" element={<ClearAlignersPage data-id="element-58" data-mp="2u21nbsxutkym" />} data-id="element-57" data-mp="2u44b06bjb206" />
        <Route path="services/orthodontics/invisalign-style-aligners" element={<InvisalignAlignersPage data-id="element-60" data-mp="21f72gux4kjkm" />} data-id="element-59" data-mp="1whf5vnljc9a3" />
        <Route path="services/orthodontics/metal-braces" element={<MetalBracesPage data-id="element-62" data-mp="26e1o04mh8m8f" />} data-id="element-61" data-mp="21g03df9ekeuu" />
        <Route path="services/orthodontics/ceramic-braces" element={<CeramicBracesPage data-id="element-64" data-mp="2b6usqshy3uki" />} data-id="element-63" data-mp="26cyv5r5lg9pv" />
        <Route path="services/orthodontics/retainers" element={<RetainersPage data-id="element-66" data-mp="1dobi2iv00qne" />} data-id="element-65" data-mp="1dqe3sqp4bxmb" />
        <Route path="services/orthodontics/children" element={<ChildrensOrthodonticsPage data-id="element-68" data-mp="3vzigfuw33nl8" />} data-id="element-67" data-mp="1iopipfu9sybe" />
        <Route path="services/:serviceId" element={<ServiceDetailPage data-id="element-70" data-mp="3vx61lqlmhtce" />} data-id="element-69" data-mp="3vy7fe2u0wtxt" />
        <Route path="prices" element={<PricesPage data-id="element-72" data-mp="3ryf25ctopzm4" />} data-id="element-71" data-mp="3rxnhuy5jfob5" />
        <Route path="price-list" element={<PrintPricePage data-id="element-74" data-mp="3myavsfmhql9m" />} data-id="element-73" data-mp="3rvstf9v25e2b" />
        <Route path="ai-support" element={<AiSupportPage data-id="element-76" data-mp="3n0x26bxnauq9" />} data-id="element-75" data-mp="3mxiyn0d8wfh8" />
        <Route path="about" element={<AboutPage data-id="element-78" data-mp="3hyo8fdcy7on3" />} data-id="element-77" data-mp="3hzzjzzcmy0or" />
        <Route path="patient-experience" element={<PatientExperiencePage data-id="element-80" data-mp="3d0eboybksfzd" />} data-id="element-79" data-mp="3hzhgqirk2m7c" />
        <Route path="results" element={<ResultsPage data-id="element-82" data-mp="3d2r1fxnkrumz" />} data-id="element-81" data-mp="3d1fz829s2uxz" />
        <Route path="reviews" element={<ReviewsPage data-id="element-84" data-mp="388v858i9avy5" />} data-id="element-83" data-mp="38a6b563vnzo7" />
        <Route path="contact" element={<ContactPage data-id="element-86" data-mp="348cct4h5n7r7" />} data-id="element-85" data-mp="388cztdibkr9v" />
        <Route path="privacy-policy" element={<PrivacyPolicyPage data-id="element-88" data-mp="348ufojybok83" />} data-id="element-87" data-mp="347t32b1nrpo9" />
        <Route path="cookie-policy" element={<CookiePolicyPage data-id="element-90" data-mp="2za14wzaf77bm" />} data-id="element-89" data-mp="2z984sh5yvg2g" />
        <Route path="patient-privacy-notice" element={<PatientPrivacyNoticePage data-id="element-92" data-mp="2uaeqimiuvl18" />} data-id="element-91" data-mp="2zb2r9zwvzg8n" />
        <Route path="terms-and-conditions" element={<TermsAndConditionsPage data-id="element-94" data-mp="2pc3jejjy3omp" />} data-id="element-93" data-mp="2uc9a9xie573q" />
        <Route path="*" element={<Navigate to={`/${lang}`} replace data-id="element-96" data-mp="2fmwuecjczyqf" />} data-id="element-95" data-mp="2pdelmexqsobp" />
      </Routes>
    </LanguageProvider>;
}
export function App() {
  return <BrowserRouter data-id="element-97" data-mp="2knkgcjsqdpds">
      <ScrollToTop data-id="element-98" data-mp="2ko3yo1mamfsc" />
      <Routes data-id="element-99" data-mp="2kk6fxh3mnq8z">
        <Route path="/business-card" element={<BusinessCardPage data-id="element-101" data-mp="26nt9d30mt99a" />} data-id="element-100" data-mp="26mroy7xq7aew" />
        <Route path="/business-card-new" element={<BusinessCardNewPage data-id="element-103" data-mp="2antn0lfwpgfa" />} data-id="element-102" data-mp="26okx5rnzm2p0" />
        <Route path="/business-card-new-ua" element={<BusinessCardNewUaPage data-id="element-105" data-mp="2ydfm78wa3vna" />} data-id="element-104" data-mp="2ap4ryol38u7z" />
        <Route path="/business-card-new-ru" element={<BusinessCardNewRuPage data-id="element-107" data-mp="33dabx94bif6r" />} data-id="element-106" data-mp="2yeh6m3z6puho" />
        <Route path="/gift-certificate" element={<GiftCertificate data-id="element-109" data-mp="2pm5kgub3st7i" />} data-id="element-108" data-mp="33c93zdilm8t9" />
        <Route path="/influencer-invite" element={<InfluencerInvitePage data-id="element-111" data-mp="2pjt3okh30ow1" />} data-id="element-110" data-mp="2pkugatdqxjfv" />
        <Route path="/raffle-voucher" element={<RaffleVoucherPage data-id="element-113" data-mp="2tledfwybkn20" />} data-id="element-112" data-mp="2tmfpo4r2nflb" />
        <Route path="/whatsapp-qr" element={<WhatsAppQrPage data-id="element-115" data-mp="3hbqubxbjlu4q" />} data-id="element-114" data-mp="3hctlm6cs1yl6" />
        <Route path="/logo" element={<LogoPage data-id="element-117" data-mp="3m3iarf0zijh5" />} data-id="element-116" data-mp="3hbiga23ooiry" />
        <Route path="/orthodontic-flyer-ua" element={<OrthodonticFlyerUaPage data-id="element-119" data-mp="3m5cx8xrwmjnc" />} data-id="element-118" data-mp="3m41v12e3xjyc" />
        <Route path="/" element={<LanguageRedirect data-id="element-121" data-mp="1amykqu1jx5u3" />} data-id="element-120" data-mp="1anrjb7qcwp15" />
        <Route path="/:lang/*" element={<LanguageRoutes data-id="element-123" data-mp="15pzu4lh3jh0n" />} data-id="element-122" data-mp="1am6qpnnlrg5x" />
        <Route path="*" element={<LanguageRedirect data-id="element-125" data-mp="15ndnco2155jh" />} data-id="element-124" data-mp="15oomgakj5p49" />
      </Routes>
    </BrowserRouter>;
}